import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input,
  Row,
  Col
} from "reactstrap";
import PropTypes from "prop-types";
import { getPlates, getPlatesOnboard } from "../../../helpers/api_rombo_helper";
import i18n from "../../../i18n";

const ModalAssign = ({
  toggle,
  isOpen,
  business_line = "PT",
  page = 1,
  size = 10,
  setNewPlates,
  services,
  onChangePlate,
  selectedService,
}) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(page);
  const [totalPages, setTotalPages] = useState(1);
  const [searchPlate, setSearchPlate] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(size);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
     
      getPlatesOnboard(business_line, currentPage, itemsPerPage, searchPlate)
        .then((response) => {
          setDrivers(response.items);
          setTotalPages(response.pages);
          setTotalItems(response.total);
          setLoading(false);
        })
        .catch((err) => {
          setError("Error loading drivers");
          setLoading(false);
        });
    }
  }, [isOpen, business_line, currentPage, itemsPerPage, searchPlate]);

  const handlePlateClick = (plate, subDriver) => {
    onChangePlate(selectedService, { value: plate, sub: subDriver });
    toggle(); 
  };

  const handlePagination = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (event) => {
    setSearchPlate(event.target.value); 
    setCurrentPage(1); 
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered={true}
      className="RequestModal"
      size="lg"
    >
      <div>
        <p className="modal-title modal-header-bottom">
          {i18n.t("PlatesToBeAssigned")}
        </p>
        <div className="modal-separator" />
        <ModalBody className="modal-scroll-body">
          <Input
            type="text"
            placeholder={i18n.t("SearchPlate")}
            value={searchPlate}
            onChange={handleSearchChange}
            className="mb-3"
            style={{ width: "30%" }}
          />
          {loading ? (
            <p>{i18n.t("Loading")}</p>
          ) : (
            <Table className="table-borderless">
              <thead>
                <tr>
                  <th className="table-header">{i18n.t("Plate")}</th>
                  <th className="table-header">{i18n.t("Driver")}</th>
                  <th className="table-header">{i18n.t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((driver, index) => (
                  <tr key={index}>
                    <td>{driver.vehicle.plate}</td>
                    <td>{driver.driver.full_name}</td>
                    <td>
                      <Button
                        className="btn btn-users border"
                        onClick={() =>
                          handlePlateClick(
                            driver.vehicle.plate,
                            driver.driver.sub
                          )
                        }
                      >
                        {i18n.t("Assign")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Row className="w-100 align-items-center">
            <Col md={6} className="text-start">
              <div className="d-flex align-items-center">
                <span className="text-muted me-2">
                  {i18n.t("Showing")} 
                </span>
                <Input
                  type="select"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="form-select-sm me-2"
                  style={{ width: '70px' }}
                >
                  <option value={5}>5</option>
                  <option value={6}>6</option>
                  <option value={7}>7</option>
                  <option value={8}>8</option>
                  <option value={7}>9</option>
                  <option value={10}>10</option>
                </Input>
                <span className="text-muted">
                  {i18n.t("of")} {totalItems} {i18n.t("TotalPlates")}
                </span>
              </div>
            </Col>
            <Col md={6} className="text-end">
              <Pagination
                aria-label="Page navigation"
                listClassName="pagination pagination-rounded justify-content-end mt-3 mb-4 pb-1"
              >
                <PaginationItem disabled={currentPage === 1}>
                  <PaginationLink
                    previous
                    onClick={() => handlePagination(currentPage - 1)}
                  />
                </PaginationItem>
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;

                  if (totalPages <= maxVisiblePages) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);

                    const start = Math.max(2, currentPage - 1);
                    const end = Math.min(totalPages - 1, currentPage + 1);

                    if (start > 2) pages.push("ellipsis-start");

                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }

                    if (end < totalPages - 1) pages.push("ellipsis-end");

                    pages.push(totalPages);
                  }
                  return pages.map((page, index) => {
                    if (page === "ellipsis-start" || page === "ellipsis-end") {
                      return (
                        <PaginationItem key={index} disabled>
                          <PaginationLink href="#">…</PaginationLink>
                        </PaginationItem>
                      );
                    }
                    return (
                      <PaginationItem key={index} active={page === currentPage}>
                        <PaginationLink onClick={() => handlePagination(page)}>
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  });
                })()}
                <PaginationItem disabled={currentPage === totalPages}>
                  <PaginationLink
                    next
                    onClick={() => handlePagination(currentPage + 1)}
                  />
                </PaginationItem>
              </Pagination>
            </Col>
          </Row>
          <Button className="btn btn-soft-secondary mx-2" onClick={toggle}>
            {i18n.t("Cancel")}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

ModalAssign.propTypes = {
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  status: PropTypes.string,
  page: PropTypes.number,
  size: PropTypes.number,
  setNewPlates: PropTypes.func.isRequired,
  services: PropTypes.array.isRequired,
  onChangePlate: PropTypes.func.isRequired,
  selectedService: PropTypes.object.isRequired, 
};

export default ModalAssign;
